import { formatCents, type BidderStanding, type Tables } from '@auctisync/shared';

// Re-exported so app code can type a standing without taking a direct
// dependency on @auctisync/shared just for one union.
export type { BidderStanding };
import type { BidMessage } from './bidding.js';
import type { AuctisyncSupabase } from './client.js';

/**
 * Reading lots from a PUBLIC (anon-reachable) surface, and keeping them live
 * (Run Brief P2.5).
 *
 * WHY THIS LIVES HERE. The website already implements all of this inside
 * `apps/web/src/lib/lotColumns.ts` and `apps/web/src/hooks/useLotSubscription.ts`,
 * tangled together with React state. The native app needs the same reads and the
 * same live-update rules, and "share LOGIC only" (Run Brief D-share) means
 * extracting them rather than re-deriving — two implementations of "what does a
 * bidder see, and when does it change" would eventually disagree about a price.
 *
 * Everything in this file is framework-neutral on purpose: no React, no timers
 * owned by a component. `subscribeToLots` hands back a plain `close()` and each
 * app wraps it in whatever its own lifecycle looks like.
 *
 * FOLLOW-UP (logged, not done here): point the website's `useLotSubscription`
 * and `lotColumns` at this module so there is one implementation. That touches
 * the live signed-out bidding path, so it is a deliberate staging-first change,
 * not a drive-by on the back of a native chunk.
 */

/**
 * The shape a public surface actually receives for a lot: the full row minus
 * the columns anon is not granted.
 *
 * Migration 20260720130000 replaced anon's blanket table-level SELECT on
 * `public.lots` with an explicit column grant, because RLS is row-level only and
 * gave the secret reserve no protection at all. Chunk-5 (20260720200000) then
 * took the bidder- and staff-identity columns off the public read too.
 *
 * Typing public state with this instead of `Tables<'lots'>` makes it a COMPILE
 * ERROR to reach for a reserve value or a bidder identity on a public surface.
 */
export type PublicLotRow = Omit<
  Tables<'lots'>,
  | 'reserve_cents'
  | 'max_bid_cents'
  | 'current_bidder_user_id'
  | 'current_tenant_buyer_id'
  | 'winning_user_id'
  | 'winning_tenant_buyer_id'
  | 'created_by'
  | 'cataloged_by_user_id'
  // 20260923210000 — the house's numbers. Jim: "MSRP is the only price bidders
  // need to see." Revoked from anon AND authenticated (asserted there).
  | 'estimate_min_cents'
  | 'estimate_max_cents'
  | 'retail_price_cents'
  // 20260922140000 — when a cataloguer finished the lot, and who. Staff-only:
  // never granted to anon (asserted in that migration).
  | 'capture_completed_at'
  | 'capture_completed_by_user_id'
  // 20261005120000 — a worker added this lot from the Cataloging app. The
  // office's to review; granted to neither shared role (asserted there).
  | 'added_in_warehouse_at'
  // 20261006130000 — which prices the house shows: the boxes are staff-only,
  // and a bidder reads the prices through the listing_* columns, filled only
  // when ticked. msrp_cents itself is still GRANTED, for the bidder-app builds
  // already handed out (they select it) — but no surface built from here on
  // may read it, or an unticked MSRP would show.
  | 'show_msrp'
  | 'show_retail_price'
  | 'show_estimate'
  | 'msrp_cents'
  // 20261006180000 — the office's own note that a lot is reviewed, and who
  // reviewed it. Staff-only; granted to neither shared role (asserted there).
  | 'reviewed'
  | 'reviewed_at'
  | 'reviewed_by_user_id'
  | 'reviewed_by_initials'
  // Jim 2026-10-14: the bin and the location notes are warehouse information,
  // staff-only (task 2026-10-14-03). Still GRANTED to anon and authenticated,
  // for the bidder-app builds already handed out (they select both): the revoke
  // waits until no released build asks for them. Staff read them through
  // lots_staff; no public surface may.
  | 'location_bin'
  | 'location_notes'
>;

/**
 * NOTE: this MUST stay a single string literal, not an array `.join()`.
 * supabase-js infers the returned row shape from the LITERAL type of the
 * argument to `.select()`; a runtime-computed string collapses inference to
 * `GenericStringError` and every field access downstream fails to typecheck.
 *
 * A `select('*')` here would fail outright for a signed-out visitor — Postgres
 * rejects `SELECT *` when the role lacks SELECT on any column — so adding a
 * column to `public.lots` means granting it to anon in a migration AND adding it
 * here. Deny-by-default is intentional.
 */
export const PUBLIC_LOT_COLUMNS =
  'id, tenant_id, auction_id, inventory_item_id, lot_number, sale_order, control_number, title, description, condition, quantity, category_id, consignor_id, starting_bid_cents, bid_increment_scale_id, scheduled_ends_at, ends_at, ended_at, extension_count, current_bid_cents, bid_count, watch_count, view_count, winning_bid_cents, winner_determined_at, reserve_met, has_reserve, reserve_satisfied, no_sale_reason, status, is_featured, is_visible, custom_fields, created_at, updated_at, ending_soon_notified_at, cataloged_at, exclude_from_platform_pool, listing_msrp_cents, listing_retail_price_cents, listing_estimate_min_cents, listing_estimate_max_cents, mpn, upc, sku, ai_description, ai_description_generated_at, ai_description_model, ai_description_source_photo_ids, consignment_profile_id, tax_exempt';

/** Fetch one lot by id. Returns null when it does not exist or is not visible. */
export async function fetchLot(
  supabase: AuctisyncSupabase,
  lotId: string,
): Promise<PublicLotRow | null> {
  const { data, error } = await supabase
    .from('lots')
    .select(PUBLIC_LOT_COLUMNS)
    .eq('id', lotId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data ?? null;
}

/** Every lot in one auction, in sale order. */
export async function fetchAuctionLots(
  supabase: AuctisyncSupabase,
  auctionId: string,
): Promise<PublicLotRow[]> {
  const { data, error } = await supabase
    .from('lots')
    .select(PUBLIC_LOT_COLUMNS)
    .eq('auction_id', auctionId)
    .eq('is_visible', true)
    .order('sale_order', { ascending: true, nullsFirst: false })
    .order('lot_number', { ascending: true, nullsFirst: false });
  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * Several lots by id, for surfaces that already know WHICH lots they want.
 *
 * The watchlist is the case this exists for: the membership rows live in
 * `bidder_watchlist` and carry no lot detail, so the ids come back first and
 * the lots are read here — through the same public column set every other read
 * uses, rather than an embedded join whose column list would be a second place
 * to keep in step with the grants.
 *
 * An id whose lot is no longer publicly readable simply does not come back.
 * That is the same thing the website's embedded join does with a withdrawn lot,
 * and callers must not assume `result.length === ids.length`.
 *
 * Returns immediately on an empty list — PostgREST would otherwise be asked for
 * `id=in.()`, which is a request for nothing.
 */
export async function fetchLotsByIds(
  supabase: AuctisyncSupabase,
  ids: readonly string[],
): Promise<PublicLotRow[]> {
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .from('lots')
    .select(PUBLIC_LOT_COLUMNS)
    .in('id', [...ids]);
  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * Lot statuses a bidder should SEE in a live feed.
 *
 * ⚠️ These are not free text. `lots_status_check` allows exactly
 * `draft | scheduled | active | closing | sold | unsold | no_sale | cancelled`
 * — there is no `open`. An earlier revision of this file filtered on `'open'`,
 * which matches no row that can exist, so the browse feed was permanently empty
 * and every lot detail rendered as closed. A mocked-database unit test cannot
 * catch that: it asserts the filter we passed, not that the value exists.
 *
 * `closing` is included because the lot is mid-close and still worth showing —
 * but see `isBiddableStatus`, which is narrower.
 */
export const LIVE_LOT_STATUSES = ['active', 'closing'] as const;

/**
 * Whether `place_bid` will accept a bid on a lot in this status.
 *
 * Deliberately NARROWER than `LIVE_LOT_STATUSES`. The RPC's gate is
 * `if v_lot.status <> 'active' then reject` (migration 20260720150000), so a
 * `closing` lot is visible but not biddable. Offering a bid the database then
 * refuses is the failure this asymmetry exists to prevent.
 */
export function isBiddableStatus(status: string | null): boolean {
  return status === 'active';
}

/**
 * The browse feed: live lots, soonest-closing first.
 *
 * `tenantId` is OPTIONAL and that is the point. Anyone — signed out included —
 * can view what is on; only bidding requires an account. Omitting the tenant
 * returns the public feed across every published house, and it is safe to do so
 * because the DATABASE does the gating, not this query: the `lots_public_select`
 * policy grants anon only visible lots, in a public status, from a published
 * tenant, whose parent auction is itself visible and live. Passing a tenant
 * simply narrows that to one house, which is what a signed-in bidder wants.
 *
 * Ordered by `ends_at` rather than lot number because this is a phone feed, not
 * a catalogue — the lot someone can still act on matters more than the lot that
 * happens to be numbered first. Lots with no close time sort last rather than
 * first, so an unscheduled lot never occupies the top of the screen.
 */
export async function fetchLiveLots(
  supabase: AuctisyncSupabase,
  options?: { tenantId?: string | null; limit?: number },
): Promise<PublicLotRow[]> {
  let query = supabase
    .from('lots')
    .select(PUBLIC_LOT_COLUMNS)
    .eq('is_visible', true)
    .in('status', [...LIVE_LOT_STATUSES]);

  if (options?.tenantId) query = query.eq('tenant_id', options.tenantId);

  const { data, error } = await query
    .order('ends_at', { ascending: true, nullsFirst: false })
    .limit(options?.limit ?? 100);
  if (error) throw new Error(error.message);
  return data ?? [];
}

/** Rows per request when reading a whole live catalogue. */
export const LIVE_LOTS_PAGE_SIZE = 500;
/**
 * The most rows `fetchAllLiveLots` reads before it stops and says so. A guard
 * against a runaway read, not a product limit: ten times DTA's 535-lot sale.
 */
export const LIVE_LOTS_CEILING = 5000;

/**
 * EVERY live lot — the Lots page's catalogue, with no 500 cap (2026-10-08).
 *
 * The 500-row cap (`MAX_LOTS` on both surfaces) cut DTA's 535-lot sale short:
 * the last 35 lots could not be searched, filtered or scrolled to. The engine's
 * feed now sends every lot's price once per open (`summary`), so the catalogue
 * — title, number, condition — has to cover every lot too.
 *
 * PAGED BY ID, not by close time. Anti-snipe moves `ends_at` while the pages
 * are being read; paging on it could skip a lot or read one twice. The id never
 * moves, so each page starts strictly after the last id read (keyset), and the
 * result is sorted soonest-closing first at the end — the order
 * `fetchLiveLots` documents, unscheduled lots last.
 *
 * `truncated` is true when the read stopped AT the ceiling — there may be more
 * — so a page can say the catalogue may be short rather than pass it off as
 * whole. A read that ran out of rows first is never truncated.
 */
export async function fetchAllLiveLots(
  supabase: AuctisyncSupabase,
  options?: { tenantId?: string | null; pageSize?: number; ceiling?: number },
): Promise<{ lots: PublicLotRow[]; truncated: boolean }> {
  const pageSize = Math.max(1, options?.pageSize ?? LIVE_LOTS_PAGE_SIZE);
  const ceiling = Math.max(pageSize, options?.ceiling ?? LIVE_LOTS_CEILING);
  const lots: PublicLotRow[] = [];
  let cursor: string | null = null;
  let truncated = false;

  for (;;) {
    let query = supabase
      .from('lots')
      .select(PUBLIC_LOT_COLUMNS)
      .eq('is_visible', true)
      .in('status', [...LIVE_LOT_STATUSES]);
    if (options?.tenantId) query = query.eq('tenant_id', options.tenantId);
    if (cursor !== null) query = query.gt('id', cursor);
    const take = Math.min(pageSize, ceiling - lots.length);
    const { data, error } = await query.order('id', { ascending: true }).limit(take);
    if (error) throw new Error(error.message);
    const page = data ?? [];
    lots.push(...page);
    const last = page[page.length - 1];
    if (page.length < take || last === undefined) break;
    if (lots.length >= ceiling) {
      truncated = true;
      break;
    }
    cursor = last.id;
  }

  lots.sort((a, b) => {
    if (a.ends_at !== b.ends_at) {
      if (a.ends_at === null) return 1;
      if (b.ends_at === null) return -1;
      return a.ends_at < b.ends_at ? -1 : 1;
    }
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
  return { lots, truncated };
}

/** The auction fields the bidding surfaces need. */
export interface AuctionBidding {
  id: string;
  default_bid_increment_scale_id: string | null;
}

/** The auction a lot belongs to, limited to what the bid surface needs. */
export async function fetchAuctionBidding(
  supabase: AuctisyncSupabase,
  auctionId: string,
): Promise<AuctionBidding | null> {
  const { data, error } = await supabase
    .from('auctions')
    .select('id, default_bid_increment_scale_id')
    .eq('id', auctionId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as AuctionBidding | null) ?? null;
}

/**
 * The smallest bid `place_bid` will accept next.
 *
 * WHY THIS IS SHARED. This is the rule a bidder is judged against, and the
 * website computes it inline in `PublicLotDetail`. If the phone were to compute
 * it even slightly differently, the app would offer a bidder an amount the
 * database then rejects — the failure lands on the bidder, in the middle of a
 * live auction, and looks like the app is broken. One implementation.
 *
 * This is a DISPLAY figure. It is not the authority and never gates the money:
 * `place_bid` re-derives the floor server-side inside the same transaction that
 * writes the ledger, so a stale value here is refused rather than honoured.
 *
 * The rules, mirrored from the web:
 *  - no increment scale at all → a flat $1.00 step (or the starting bid if the
 *    lot has no bid yet)
 *  - no bid yet → the starting bid IS the minimum, not starting + increment
 *  - otherwise → current + `bid_increment_for_amount(scale, current)`
 */
/**
 * Is this sale over?
 *
 * A finished catalogue is a different screen — the design's locked rule is
 * "show results but do not route into live Browse" — so the status has to be
 * known before the catalogue decides how to behave. Its own small read rather
 * than a field on the lot rows: the lots do not carry their auction's status,
 * and inferring "ended" from every lot being closed would call a sale finished
 * the moment its last lot closed but before the house had settled it.
 */
export async function fetchAuctionStatus(
  supabase: AuctisyncSupabase,
  auctionId: string,
): Promise<string | null> {
  const { data, error } = await supabase
    .from('auctions')
    .select('id, status')
    .eq('id', auctionId)
    .maybeSingle();
  if (error) return null;
  return data?.status ?? null;
}

export async function fetchNextMinimumCents(
  supabase: AuctisyncSupabase,
  lot: Pick<PublicLotRow, 'current_bid_cents' | 'starting_bid_cents' | 'bid_increment_scale_id'>,
  auctionDefaultScaleId: string | null,
): Promise<number> {
  const scaleId = lot.bid_increment_scale_id ?? auctionDefaultScaleId;
  const current = lot.current_bid_cents;

  if (!scaleId) return (current ?? lot.starting_bid_cents) + 100;
  if (current === null) return lot.starting_bid_cents;

  const { data } = await supabase.rpc('bid_increment_for_amount', {
    p_scale_id: scaleId,
    p_current_cents: current,
  });
  // The RPC can come back as a numeric string over PostgREST. Falling back to
  // 100 rather than NaN keeps a bad response from rendering "$NaN" on a
  // bidder's screen.
  const increment = typeof data === 'number' ? data : Number(data ?? 100);
  return current + (Number.isFinite(increment) ? increment : 100);
}

export type LotSubscriptionTarget =
  | { kind: 'lot'; lotId: string }
  | { kind: 'auction'; auctionId: string }
  | { kind: 'tenant'; tenantId: string }
  // The signed-out public feed. Subscribes to every lot UPDATE; WALRUS applies
  // the same RLS + column redaction anon gets on a normal read, so this cannot
  // surface a lot (or a column) a visitor could not already query.
  | { kind: 'public' };

export interface SubscribeToLotsOptions {
  target: LotSubscriptionTarget;
  /** Called with each lot row that actually changed (deduped). */
  onUpdate: (lot: PublicLotRow) => void;
  /** Called when the live channel connects or drops, for an on-air indicator. */
  onLiveChange?: (isLive: boolean) => void;
  /**
   * Fallback poll interval used ONLY while the channel is disconnected. The
   * happy path does not poll at all. Default 3000ms.
   */
  disconnectedPollMs?: number;
}

export interface LotSubscription {
  /** Tears down the channel, the retry timer and the poll timer. Idempotent. */
  close: () => void;
}

const MAX_RETRIES = 5;

function targetKey(target: LotSubscriptionTarget): string {
  switch (target.kind) {
    case 'lot':
      return target.lotId;
    case 'auction':
      return target.auctionId;
    case 'tenant':
      return target.tenantId;
    default:
      return 'all';
  }
}

/** `undefined` means "no filter" — every row the subscriber is allowed to see. */
function targetFilter(target: LotSubscriptionTarget): string | undefined {
  switch (target.kind) {
    case 'lot':
      return `id=eq.${target.lotId}`;
    case 'auction':
      return `auction_id=eq.${target.auctionId}`;
    case 'tenant':
      return `tenant_id=eq.${target.tenantId}`;
    default:
      return undefined;
  }
}

/**
 * Subscribe to UPDATEs on `public.lots`, with a polling fallback.
 *
 * While the channel is SUBSCRIBED we trust the stream and do not poll. The
 * moment it drops we poll every `disconnectedPollMs` until it comes back, so
 * staleness stays bounded through a reconnect instead of the price silently
 * freezing — which on a bidding screen is the difference between losing a lot
 * and knowing you were outbid.
 *
 * Realtime can transiently CHANNEL_ERROR a subscription created right after
 * another one tore down on the same client (navigating browse → lot does
 * exactly that), so failures retry with backoff under a fresh channel name.
 *
 * The identity columns are NOT a leak risk on this path: Supabase filters
 * postgres_changes through WALRUS, which redacts columns the subscribing role
 * lacks SELECT on. Verified empirically — see `scripts/verify-lots-realtime-leak.mjs`.
 */
export function subscribeToLots(
  supabase: AuctisyncSupabase,
  { target, onUpdate, onLiveChange, disconnectedPollMs = 3000 }: SubscribeToLotsOptions,
): LotSubscription {
  let cancelled = false;
  let retryAttempts = 0;
  let activeChannel: ReturnType<AuctisyncSupabase['channel']> | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  const key = targetKey(target);
  const filter = targetFilter(target);
  const baseName = `${target.kind}-${key}-lots`;

  // Realtime redelivers, and a poll tick right after a live event sees the same
  // row again. Without this every reconnect would re-render the whole list.
  const lastSeen = new Map<string, string>();
  const stampOf = (row: PublicLotRow) =>
    `${row.updated_at ?? ''}|${row.bid_count}|${row.current_bid_cents ?? ''}|${row.status}|${row.ends_at ?? ''}`;

  const handle = (row: PublicLotRow) => {
    const stamp = stampOf(row);
    if (lastSeen.get(row.id) === stamp) return;
    lastSeen.set(row.id, stamp);
    onUpdate(row);
  };

  async function pollOnce(): Promise<void> {
    if (cancelled) return;
    try {
      if (target.kind === 'lot') {
        const row = await fetchLot(supabase, target.lotId);
        if (!cancelled && row) handle(row);
        return;
      }
      const rows =
        target.kind === 'auction'
          ? await fetchAuctionLots(supabase, target.auctionId)
          : await fetchLiveLots(
              supabase,
              target.kind === 'tenant' ? { tenantId: target.tenantId } : {},
            );
      if (cancelled) return;
      for (const row of rows) handle(row);
    } catch {
      // A failed fallback poll is not fatal — the next tick retries, and the
      // channel may well be back by then. Throwing here would take down the
      // screen for a transient network blip.
    }
  }

  const startPolling = () => {
    if (pollTimer !== null) return;
    pollTimer = setInterval(() => void pollOnce(), disconnectedPollMs);
  };
  const stopPolling = () => {
    if (pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  const subscribe = (): void => {
    if (cancelled) return;
    // A unique name per attempt so the server does not collide the retry with a
    // lingering channel of the same name.
    const channel = supabase
      .channel(`${baseName}-${retryAttempts}-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'lots',
          ...(filter !== undefined ? { filter } : {}),
        },
        (payload: { new: unknown }) => {
          if (cancelled) return;
          handle(payload.new as PublicLotRow);
        },
      )
      .subscribe((status: unknown) => {
        if (cancelled) return;
        const s = String(status);
        if (s === 'SUBSCRIBED') {
          retryAttempts = 0;
          onLiveChange?.(true);
          stopPolling();
          return;
        }
        onLiveChange?.(false);
        startPolling();
        if ((s === 'CHANNEL_ERROR' || s === 'TIMED_OUT') && retryAttempts < MAX_RETRIES) {
          retryAttempts += 1;
          const delay = Math.min(500 * 2 ** (retryAttempts - 1), 5000);
          if (retryTimer) clearTimeout(retryTimer);
          retryTimer = setTimeout(() => {
            if (cancelled) return;
            if (activeChannel) void supabase.removeChannel(activeChannel);
            activeChannel = null;
            subscribe();
          }, delay);
        }
      });
    activeChannel = channel;
  };

  subscribe();

  return {
    close: () => {
      if (cancelled) return;
      cancelled = true;
      stopPolling();
      if (retryTimer) clearTimeout(retryTimer);
      if (activeChannel) void supabase.removeChannel(activeChannel);
      activeChannel = null;
      onLiveChange?.(false);
    },
  };
}

/**
 * Server-authoritative clock.
 *
 * The device clock cannot be trusted to decide when a lot closes — the SERVER
 * decides, and a phone running 90 seconds fast would show a bidder a countdown
 * that hits zero while the lot is still open. This samples
 * `public.get_server_time()` and corrects for round-trip so `nowMs()` reads as
 * the server sees it.
 *
 * Framework-neutral: it owns its own interval and hands back a `stop()`.
 */
export interface ServerClock {
  /** Server-corrected wall clock, safe to call inside a render or a tick. */
  nowMs: () => number;
  /** Current correction in ms, for diagnostics. */
  offsetMs: () => number;
  stop: () => void;
}

export function startServerClock(
  supabase: AuctisyncSupabase,
  options?: { resyncMs?: number },
): ServerClock {
  let offset = 0;
  let cancelled = false;

  async function syncOnce(): Promise<void> {
    const sentAt = Date.now();
    const { data } = await supabase.rpc('get_server_time');
    if (cancelled || !data) return;
    const rtt = Date.now() - sentAt;
    // Assume the server observed the time at the midpoint of the round trip;
    // taking the arrival time instead would bake the whole latency into the
    // offset and read consistently late.
    offset = new Date(data).getTime() - (sentAt + rtt / 2);
  }

  void syncOnce();
  const timer = setInterval(() => void syncOnce(), options?.resyncMs ?? 60_000);

  return {
    nowMs: () => Date.now() + offset,
    offsetMs: () => offset,
    stop: () => {
      cancelled = true;
      clearInterval(timer);
    },
  };
}

/**
 * The caller's standing on each lot in an auction (Run Brief P2.6).
 *
 * WHY THIS MATTERS MORE THAN IT LOOKS. Proxy bidding means the price a bidder
 * sees after bidding is often NOT the number they typed — they enter a ceiling
 * and the server bids the least it needs to. Without a standing badge the only
 * feedback is a price that moved to an amount they never entered, which reads
 * as the app doing something wrong. (Observed exactly that on 2026-07-31.)
 *
 * `get_my_lot_standings` resolves `auth.uid()` server-side and returns BOOLEANS
 * ONLY — no bidder id crosses the wire, which is what allowed the identity
 * columns to be revoked from anon in the first place. It returns nothing to an
 * unauthenticated caller, so a signed-out viewer simply has no standings.
 */
export interface LotStandingFlags {
  /** This viewer is the current high bidder, or the crowned winner. */
  iLead: boolean;
  /** The lot was won by somebody else. */
  otherWon: boolean;
  /**
   * This viewer has a live (non-rejected) bid on the lot.
   *
   * 🔴 THE RPC HAS RETURNED THIS SINCE 2026-09-18 AND NOTHING READ IT. It was
   * added so the TILES could use the same rule as the standing counts —
   * otherwise a header reports outbids that no card is tagged with. Until now
   * every caller passed its own `hasBid` from a second query; the flag makes
   * that second query optional.
   */
  iBid: boolean;
}

export async function fetchMyLotStandings(
  supabase: AuctisyncSupabase,
  auctionId: string,
): Promise<Map<string, LotStandingFlags>> {
  const { data, error } = await supabase.rpc('get_my_lot_standings', {
    p_auction_id: auctionId,
  });
  if (error) throw new Error(error.message);

  const flags = new Map<string, LotStandingFlags>();
  for (const row of (data ?? []) as Array<{
    lot_id: string;
    i_lead: boolean;
    other_won: boolean;
    i_bid: boolean | null;
  }>) {
    flags.set(row.lot_id, {
      iLead: row.i_lead,
      otherWon: row.other_won,
      // Null-tolerant: an environment whose function predates the 2026-09-18
      // migration returns three columns, and "no bid" is the safe reading.
      iBid: row.i_bid === true,
    });
  }
  return flags;
}

/**
 * Standings across SEVERAL sales, merged into one map.
 *
 * 🔴 THE PHONE'S FEED IS SCOPED BY HOUSE, NOT BY SALE. `get_my_lot_standings`
 * takes one auction id and always has; the website's live room is a page per
 * sale so it never needed more. The app's merged Lots screen shows every open
 * sale a house is running, so it asks per sale and merges — the same shape the
 * bid ticker uses, and for the same reason.
 *
 * Lot ids are unique across sales, so the merge cannot collide.
 *
 * A sale that FAILS is skipped rather than taking the others down: a missing
 * standing costs a badge, and throwing would cost the whole screen its
 * standings because one sale misbehaved.
 */
export async function fetchMyLotStandingsForSales(
  supabase: AuctisyncSupabase,
  auctionIds: readonly string[],
): Promise<Map<string, LotStandingFlags>> {
  const merged = new Map<string, LotStandingFlags>();
  const results = await Promise.allSettled(
    auctionIds.map((id) => fetchMyLotStandings(supabase, id)),
  );
  for (const r of results) {
    if (r.status !== 'fulfilled') continue;
    for (const [lotId, flags] of r.value) merged.set(lotId, flags);
  }
  return merged;
}

/**
 * The viewer's standing on a LIST of lots, across sales (2026-10-08).
 *
 * 🔴 WHY THIS EXISTS. Since 2026-09-23 a signed-in bidder may not read
 * `lots.current_bidder_user_id` or `winning_user_id` — and My bids, Account
 * overview, the sale page's "you won", the sales list's standing line and the
 * app's dashboard all still selected them, so every one of those reads failed
 * with a permission error for every bidder. `get_my_standings_for_lots` gives
 * the same answer as booleans, for exactly the lots asked about, in one call:
 * the per-sale `fetchMyLotStandings` would return every lot of every sale a
 * bidder ever touched to answer for the few they bid on.
 *
 * Throws on any failure: a bidder's own standing is not something to guess.
 */
export interface MyLotStanding extends LotStandingFlags {
  /** The lot SOLD and this viewer is its crowned winner. */
  iWon: boolean;
}

/** `get_my_standings_for_lots` refuses more than 1000 lots a call; ask in pages of this many. */
export const MY_STANDINGS_PAGE = 500;

export async function fetchMyStandingsForLots(
  supabase: AuctisyncSupabase,
  lotIds: readonly string[],
): Promise<Map<string, MyLotStanding>> {
  const ids = [...new Set(lotIds)];
  const pages: string[][] = [];
  for (let i = 0; i < ids.length; i += MY_STANDINGS_PAGE) {
    pages.push(ids.slice(i, i + MY_STANDINGS_PAGE));
  }
  const results = await Promise.all(
    pages.map((page) => supabase.rpc('get_my_standings_for_lots', { p_lot_ids: page })),
  );

  const out = new Map<string, MyLotStanding>();
  for (const { data, error } of results) {
    if (error) throw new Error(error.message);
    for (const row of data ?? []) {
      out.set(row.lot_id, {
        iLead: row.i_lead,
        otherWon: row.other_won,
        iBid: row.i_bid,
        iWon: row.i_won,
      });
    }
  }
  return out;
}

/**
 * The viewer's standing on a lot, or undefined when there is nothing to say.
 *
 * The RPC alone CANNOT express "outbid": a bidder who bid and was overtaken on
 * a still-live lot looks identical to one who never bid — both are
 * `iLead=false, otherWon=false`. Combining it with whether this viewer has any
 * bid on the lot resolves it, so no migration is needed to show what is
 * arguably the most important state a bidder can be in: knowing they have LOST
 * the lead is what brings them back.
 *
 * Returns undefined rather than 'watching' for a non-bidder — 'watching' means
 * on the watchlist, which is a different thing entirely.
 */
export function resolveStanding(
  flags: LotStandingFlags | undefined,
  hasBid?: boolean,
): BidderStanding | undefined {
  if (flags?.iLead) return 'winning';
  if (flags?.otherWon) return 'outbid';
  // `hasBid` came from a SECOND query, back when the RPC could not say. It
  // still wins when a caller passes it — the lot page knows its own position
  // more precisely than a per-sale sweep does — but a caller with only the
  // flags now gets the right answer from `iBid` instead of always `undefined`.
  if (hasBid ?? flags?.iBid ?? false) return 'outbid';
  return undefined;
}

/**
 * What THIS bidder has on a lot (Run Brief P2.6 follow-up).
 *
 * Filtered to the signed-in account's OWN rows, explicitly. Row-level
 * security is not that filter: `bids_self_select` returns a bidder's own rows,
 * but `bids_tenant_staff_select` returns EVERY bid at the house to its staff
 * and to super admins — so a house owner browsing their own sale was shown
 * the leader's maximum as "Your maximum" and tagged "Outbid" on a lot they
 * never bid on (Jim, 2026-10-07, during Stress 1). RLS is the gate on what
 * may be read; which rows mean "mine" is this filter's job. Signed out: no
 * query, no position.
 */
export interface MyLotPosition {
  hasBid: boolean;
  /**
   * The highest ceiling this bidder holds — their proxy maximum. Secret from
   * rivals, but it must NOT be secret from them: not showing it is how a bidder
   * ends up re-bidding under their own ceiling and seeing nothing happen.
   */
  ceilingCents: number | null;
  /** The highest amount actually posted on their behalf. */
  highestBidCents: number | null;
}

export async function fetchMyLotPosition(
  supabase: AuctisyncSupabase,
  lotId: string,
): Promise<MyLotPosition> {
  const { data: auth } = await supabase.auth.getSession();
  const userId = auth.session?.user.id;
  if (!userId) return { hasBid: false, ceilingCents: null, highestBidCents: null };

  // Both records, because a lot is in exactly one of them at a time: a sale
  // the auction ENGINE runs writes its bids to `bids` when the lot closes
  // (20261008222000); until then the bidder's own ceiling is in
  // `bidder_lot_positions` (see `fetchMyEnginePositions`).
  const [{ data, error }, positions] = await Promise.all([
    supabase
      .from('bids')
      .select('amount_cents, max_amount_cents')
      .eq('lot_id', lotId)
      .eq('user_id', userId)
      .eq('is_rejected', false),
    fetchMyEnginePositions(supabase, userId, { lotId }),
  ]);
  if (error) throw new Error(error.message);

  const rows = (data ?? []) as Array<{ amount_cents: number; max_amount_cents: number | null }>;
  if (rows.length === 0 && positions.length === 0) {
    return { hasBid: false, ceilingCents: null, highestBidCents: null };
  }

  let ceiling: number | null = null;
  let highest: number | null = null;
  for (const row of rows) {
    ceiling = Math.max(ceiling ?? 0, row.max_amount_cents ?? row.amount_cents);
    highest = Math.max(highest ?? 0, row.amount_cents);
  }
  for (const p of positions) {
    if (typeof p.ceilingCents === 'number') ceiling = Math.max(ceiling ?? 0, p.ceilingCents);
  }
  return { hasBid: true, ceilingCents: ceiling, highestBidCents: highest };
}

/**
 * Where the auction engine says THIS bidder stands, lot by lot, on sales it is
 * running (2026-10-08).
 *
 * `bidder_lot_positions` is written by the engine's notifier
 * (`private.engine_notify_batch`) and settled from the ledger when each lot
 * closes; row-level security returns only the caller's own rows, and the
 * explicit `user_id` filter keeps "mine" meaning mine for staff too (the same
 * reason `fetchMyLotPosition` filters bids). `ceilingCents` is null where the
 * engine has not said — a bid that lost at once to a higher hidden maximum.
 *
 * A database without the table yet (a deployment ahead of its migration —
 * website previews read production) answers as "no positions", so the page
 * keeps today's behaviour there. Every other error is thrown, as the bids read
 * throws: a bidder's own standing is not something to guess.
 */
export interface MyEnginePosition {
  lotId: string;
  saleId: string;
  leading: boolean;
  ceilingCents: number | null;
  updatedAt: string;
}

/** PostgREST / Postgres codes for "that table is not in this database". */
const MISSING_RELATION_CODES = new Set(['PGRST205', '42P01']);

/** True for the error a database without the table answers with. */
export function isMissingRelationError(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' && MISSING_RELATION_CODES.has(code);
}

export async function fetchMyEnginePositions(
  supabase: AuctisyncSupabase,
  userId: string,
  opts: { lotId?: string } = {},
): Promise<MyEnginePosition[]> {
  let query = supabase
    .from('bidder_lot_positions')
    .select('lot_id, sale_id, is_leading, ceiling_cents, updated_at')
    .eq('user_id', userId);
  if (opts.lotId !== undefined) query = query.eq('lot_id', opts.lotId);
  const { data, error } = await query;
  if (error) {
    if (isMissingRelationError(error)) return [];
    throw new Error(error.message);
  }
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    lotId: String(r.lot_id),
    saleId: String(r.sale_id),
    leading: r.is_leading === true,
    ceilingCents: typeof r.ceiling_cents === 'number' ? r.ceiling_cents : null,
    updatedAt: typeof r.updated_at === 'string' ? r.updated_at : '',
  }));
}

/**
 * One sentence telling a bidder where they stand on a lot.
 *
 * SHARED so the phone and the website say the SAME thing. A bidder who reads
 * one figure on their phone and another on the site has no way to know which to
 * believe about their own money.
 *
 * ALWAYS THE CEILING, never `highestBidCents`. `amount_cents` is the floor a
 * bid posts — an internal mechanic of proxy resolution, not a number the bidder
 * chose. Showing it understates what they committed: a bidder who set $127.00
 * and was beaten at $128.00 was told "Your highest bid: $51.00", because the
 * proxy never needed to post more than $51.00 to hold the lot. That reads as
 * having lost to a bid barely above their own. (Jim, 2026-07-31.)
 *
 * Only the tense changes:
 *  - never bid           -> say so plainly
 *  - ceiling above price -> an ACTIVE maximum is holding for them
 *  - ceiling at or below -> that maximum has been beaten; past tense
 */
export function describeMyPosition(
  position: MyLotPosition,
  currentBidCents: number | null,
): string {
  if (!position.hasBid || position.ceilingCents === null) {
    return 'You have not bid on this lot.';
  }
  const price = currentBidCents ?? 0;
  return position.ceilingCents > price
    ? `Your maximum: ${formatCents(position.ceilingCents)}`
    : `Your maximum was ${formatCents(position.ceilingCents)}`;
}

/**
 * Why a new ceiling is pointless BEFORE it is sent, or null when it is fine.
 *
 * `place_bid` currently ACCEPTS a ceiling below one the bidder already holds:
 * it writes a ledger row, changes nothing, and returns success. That is how a
 * bidder ends up tapping Bid three times wondering why the price will not move
 * (observed 2026-07-31). Catching it here turns a silent no-op into an
 * explanation. It is a client-side courtesy, not a security control — the
 * server remains the authority on every bid it accepts.
 */
export function explainPointlessBid(
  position: MyLotPosition,
  newCeilingCents: number,
): BidMessage | null {
  if (!position.hasBid || position.ceilingCents === null) return null;
  if (newCeilingCents > position.ceilingCents) return null;
  // A NOTICE, not an error. Nothing has gone wrong: the bidder's maximum is
  // recorded and working. Rendering this in red made a successful bid look
  // like a failed one (reported 2026-08-05, from a real device).
  //
  // So the sentence opens by CONFIRMING the maximum is in place, and only then
  // says what raising it would take. "You already have…" led with the refusal.
  return {
    tone: 'notice',
    text: `Your maximum of ${formatCents(
      position.ceilingCents,
    )} is already in. To raise it, enter more than that.`,
  };
}
